/**
 * Die Symbolleiste — der dauerhafte Einstieg in die Bereiche (ADR 0011).
 *
 * Sie ersetzt die frühere Regel „drei Bildschirme, Navigation nur über den
 * Objektbaum". Der Baum beantwortet zuverlässig „was steckt in diesem Gebäude";
 * er beantwortet nicht „alle überfälligen Forderungen über alles hinweg". Genau
 * dafür sind die Bereiche da.
 *
 * **Rein darstellend.** `components/` darf `features/` nicht importieren
 * (eslint-boundaries), die Zähler kommen aber später aus den Features. Die
 * Einträge und das Konto sind deshalb Eingaben, verdrahtet wird in
 * `router.tsx` — dieselbe Aufteilung wie beim `tree`-Slot.
 *
 * **Nur Bereiche, die es gibt.** ADR 0011/0017 nennen fünf als Zielzustand;
 * hier stehen die, deren Feature existiert. Geld, Aufgaben, Kalender und
 * Dokumente kamen mit Feature 04, 06, 07 und 09 — als je eine Zeile in
 * `router.tsx`, nicht als Umbau. Eine Symbolleiste mit leeren Bereichen wäre
 * ein Versprechen, das die Oberfläche nicht hält.
 *
 * Schreibtisch-only: Unter 768px navigiert die Tab-Leiste in `AppShell`, mit
 * denselben drei Zielen wie iOS.
 */

import { Link, useRouterState } from '@tanstack/react-router'
import type { ComponentType, ReactNode } from 'react'

export interface RailItem {
  readonly to: string
  readonly label: string
  readonly testId: string
  readonly Icon: ComponentType<{ className?: string }>
  /**
   * Was Aufmerksamkeit braucht — nicht die Gesamtzahl. Ist alles erledigt,
   * steht hier nichts; eine leere Symbolleiste ist eine gute Nachricht.
   * Die Zahl kommt vom Server, nie aus einem `.filter().length` im Client.
   */
  readonly attention?: number
}

interface RailProps {
  readonly items: readonly RailItem[]
  readonly account: ReactNode
}

export function Rail({ items, account }: RailProps) {
  const path = useRouterState({ select: (state) => state.location.pathname })

  return (
    <nav
      aria-label="Bereiche"
      data-testid="rail"
      // `print:hidden` — die Symbolleiste gehört zur Hülle, nicht zum
      // druckbaren Nachweis des Mietkontos (Spec 04c, „Drucken").
      className="border-border bg-bg hidden w-14 shrink-0 flex-col items-center gap-1 border-r py-2 md:flex print:hidden"
    >
      {items.map((item) => (
        <RailLink key={item.to} item={item} isActive={isActive(item.to, path)} />
      ))}
      {/* Das Konto sitzt unten, weil es zu keinem Bereich gehört. In der
          Kontext-Sidebar wäre es an einen Bereich gebunden und sähe je nach
          Ansicht wie ein weiterer Eintrag darin aus. */}
      <div className="mt-auto">{account}</div>
    </nav>
  )
}

/** „/" nur bei exakter Übereinstimmung — sonst wäre jeder Pfad „Heute". */
function isActive(to: string, path: string): boolean {
  return to === '/' ? path === '/' : path.startsWith(to)
}

function RailLink({ item, isActive: active }: { item: RailItem; isActive: boolean }) {
  const { Icon } = item

  return (
    <Link
      to={item.to}
      data-testid={item.testId}
      aria-current={active ? 'page' : undefined}
      title={item.label}
      className={`rounded-card relative flex w-10 flex-col items-center justify-center gap-0.5 py-1.5 transition-colors duration-(--duration-fast) ease-(--ease-out) ${
        active ? 'bg-accent/10 text-accent' : 'text-muted hover:text-text'
      }`}
    >
      <Icon className="size-4" />
      {/* `tracking-tighter`: „Dokumente" maß 58px in der 56px breiten Schiene
       * — „D" am linken Bildrand angeschnitten, „e" über der rechten Trennlinie
       * (W-D1, `reports/18-design-reviewer.md`). Kein neuer Font-Token, keine
       * Abkürzung (bräche die Wortgleichheit mit iOS) — nur engerer Zeichen-
       * abstand, für alle Einträge einheitlich statt nur für den einen Fund. */}
      <span className="text-label leading-tight tracking-tighter">{item.label}</span>
      {item.attention !== undefined && item.attention > 0 && (
        <span
          data-testid={`${item.testId}-attention`}
          aria-label={`${String(item.attention)} brauchen Aufmerksamkeit`}
          className="bg-status-overdue absolute top-1 right-1.5 size-1.5 rounded-full"
        />
      )}
    </Link>
  )
}
